import { IS_AWS_ENV } from "constants/app.constants";

export enum EvaChart {
  RateCum = "Rate Cum",
  RateTime = "Rate Time",
  RateDate = "Rate Date",
  TotalRateDate = "Total Rate Date",
  CumTime = "Cum Time",
  CrossPlot = "Cross Plot",
  Probit = "Probit",
  BoxPlot = "Box Plot",
  StackedBar = "Stacked Bar",
  StackedBarHorizontal = "Horizontal Stacked Bar",
  Pie = "Pie",
  Mosaic = "Mosaic",
  MaterialBalanceTime = "Material Balance Time",
  TotalRateCum = "Total Rate Cum",
  CAGR = "CAGR",
  DeclineRate = "Decline Rate",
  TrendDate = "Trend Date",
  WellContribution = "Well Contribution Midstream",
  RateDateMidstream = "Rate Date Midstream",
  TotalRateDateMidstream = "Total Rate Date Midstream",
  TrendDateMidstream = "Trend Date Midstream",
  CrossPlotMidstream = "Cross Plot Midstream",
  MosaicMidstream = "Mosaic Midstream",
  StackedBarMidstream = "Stacked Bar Midstream",
  BoxPlotMidstream = "Box Plot Midstream",
  RecoveryPVI = "RF vs PV",
  RecoveryTime = "RF vs Time",
  InjectorTotal = "Injector Count Date",
  RigFracActivity = "Activity vs Time",
  RigFracRecentActivity = "Recent Activity",
  WellStatusOverTime = "Well Status vs Time",
  // This value MUST equal ALL_CHART_TYPES.WellStatusCurrent.label. The chart-type
  // menu builds its items from those labels and dispatches them straight back as
  // EvaChart values (WellStatusChartSelector), so the label is the widget's
  // IDENTITY, not just its caption — decoupling the two makes the menu entry
  // dispatch a chart type nothing matches, and the widget silently stays on Over
  // Time. A test pins the pair.
  //
  // It is also the persisted key useWellStatusSession writes into a saved
  // dashboard, so moving it strands anything already saved under the old value.
  // Between those two constraints this string is effectively frozen; rename the
  // heading via WELL_STATUS_CURRENT_TITLE instead.
  WellStatusCurrent = "Well Status Current",
  None = "None"
}

export enum EvaChartId {
  CrossPlot = 0,
  RateCum = 1,
  RateTime = 2,
  RateDate = 3,
  TotalRateDate = 4,
  Probit = 5,
  BoxPlot = 6,
  Pie = 7,
  StackedBar = 8,
  Mosaic = 9,
  CumTime = 10,
  MaterialBalanceTime = 11,
  TotalRateCum = 12,
  CAGR = 13,
  DeclineRate = 14,
  TrendDate = 15,
  WellContribution = 16,
  RecoveryPVI = 17,
  RecoveryTime = 18,
  RigFracActivity = 19,
  RigFracRecentActivity = 20,
  InjectorTotal = 21,
  WellStatusOverTime = 22,
  WellStatusCurrent = 23
  // Deliberately no StackedBarHorizontal entry — like StackedBarMidstream, it
  // reuses StackedBar's wire id (chart-svc's ChartType enum has no entry for
  // it; the rotation is a frontend-only rendering concern). See
  // ALL_CHART_TYPES.StackedBarHorizontal in chart.constants.tsx.
}

// Rig/Frac Activity chart (EVA-76, EVA-149) — lean request enums; string values
// are the wire contract with chart-svc POST api/v1/rigFracChart.
//
// WtiPrice/HenryHubPrice/None (EVA-149): string values are the C# member names
// in PascalCase, NOT camelCase — JsonStringEnumConverter(CamelCase) camelCases
// on write but matches case-insensitively on read, and existing stored
// dashboards already hold PascalCase values (RigCount/FracCrewCount) for this
// enum. Do not "correct" these to camelCase.
export enum RigFracActivityMetric {
  RigCount = "RigCount",
  FracCrewCount = "FracCrewCount",
  WtiPrice = "WtiPrice",
  HenryHubPrice = "HenryHubPrice",
  // EVA-105. AWS only; counted server-side from the well filter.
  ApprovedPermits = "ApprovedPermits",
  // Explicit string member (never an absent/undefined field) — see
  // requests/rigFracActivity.ts and useRigFracActivityController.ts for why
  // an absent secondaryMetric would otherwise resurrect the FracCrewCount default.
  None = "None"
}

export enum RigFracGroupBy {
  Operator = "Operator",
  Basin = "Basin",
  SubBasin = "SubBasin",
  Country = "Country",
  StateProvince = "StateProvince",
  County = "County",
  LocationType = "LocationType",
  // EVA-107. Rig-count only — a frac crew has no rig, so grouping frac activity
  // this way collapses to a single "n/a" series. Gated in RigFracGroupBySelector.
  RigTrajectory = "RigTrajectory"
}

export enum RigFracTimeScale {
  Weekly = "Weekly",
  Monthly = "Monthly",
  Quarterly = "Quarterly",
  Custom = "Custom"
}

// Well Status widget — lean request enums; string values are the wire
// contract with chart-svc POST api/v1/wellStatusChart.

/**
 * Derived well statuses for the over-time chart, in life-of-a-well order.
 * Fixed vocabulary (unlike the current-status chart, whose options come from the
 * data): these are the statuses the server's date-derivation can produce.
 * DRAIN variants are distinct selections — parent statuses exclude drain legs.
 */
export enum WellStatus {
  Lic = "LIC",
  IdleLic = "IDLE LIC",
  Spud = "SPUD",
  Idle = "IDLE",
  Duc = "DUC",
  Cmpltd = "CMPLTD",
  Prod = "PROD",
  Inj = "INJ",
  Si = "SI",
  Susp = "SUSP",
  Abd = "ABD",
  DrainDuc = "DRAIN (DUC)",
  DrainProd = "DRAIN (PROD)",
  DrainInj = "DRAIN (INJ)",
  DrainSi = "DRAIN (SI)",
  DrainSusp = "DRAIN (SUSP)"
}

/**
 * Statuses selectable on the OVER-TIME chart, in life-of-a-well order.
 *
 * The DRAIN variants are deliberately NOT offered — product asked to
 * "Remove Drain status from selector" — and stay off this list. They remain in
 * {@link WellStatus} and in the server's vocabulary as a published contract,
 * but the derivation no longer emits them (EVA-120): with the leg-to-parent-well
 * date join in place, a multilateral leg now counts under its PARENT's status
 * (PROD, SUSP, ...) rather than under a dead DRAIN (x) bucket.
 *
 * `includeDrainLegs` (toggled via the toolbar's drain-legs control,
 * DrainLegsToggle) governs whether that join runs at all: `true` counts legs
 * under their parent status; `false`, the default, drops legs from the chart
 * entirely. The DRAIN options do NOT return to this selector under either
 * setting — a leg is never itself a selectable status, only counted under one.
 */
export const WELL_STATUS_OVER_TIME_OPTIONS: readonly WellStatus[] = [
  WellStatus.Lic,
  WellStatus.IdleLic,
  WellStatus.Spud,
  WellStatus.Idle,
  WellStatus.Duc,
  WellStatus.Cmpltd,
  WellStatus.Prod,
  WellStatus.Inj,
  WellStatus.Si,
  WellStatus.Susp,
  WellStatus.Abd
];

/**
 * The "show every status" entry in the status dropdown: instead of filtering to a
 * single status, break every status held by the filtered wells out as its own
 * stack segment.
 *
 * UI-ONLY. This literal never reaches the wire — an in-band sentinel `status` was
 * replaced by an explicit `allStatuses` boolean on the request, so there is no
 * magic string for the server to agree with. {@link WellStatusSelector} is the
 * only place it is interpreted: picking it sets the flag and blanks `status`.
 *
 * Spelled out rather than an empty string so it reads as a deliberate selection
 * in the dropdown state, not an absent one.
 */
export const ALL_WELL_STATUSES = "All Statuses";

/**
 * The current-status chart's on-chart heading and data-table caption.
 *
 * Currently the same string as `EvaChart.WellStatusCurrent`, but read from here
 * rather than from the enum on purpose: the enum's value is load-bearing as both
 * the chart-type menu's dispatched identity and the persisted key in a saved
 * dashboard, so it cannot move. Reading the heading from its own constant means a
 * future wording change touches only what is displayed.
 *
 * The chart-type MENU still shows the enum value, because that menu renders the
 * same string it dispatches — changing what it displays needs the menu to carry
 * identity and caption separately, which it does not today.
 */
export const WELL_STATUS_CURRENT_TITLE = "Well Status Current";

/**
 * Azure statuses selectable on the CURRENT-status chart, as VERBATIM
 * `header.status` values. AWS uses {@link AWS_WELL_STATUSES} instead.
 *
 * Hardcoded rather than discovered from the data (product decision: "it would be
 * a pain to determine what is available otherwise"), so there is no
 * distinct-values fetch and no GetStatusValues RPC.
 *
 * IMPORTANT — these are NOT bare abbreviations. Azure's stored `header.status`
 * strings carry a two-digit stage prefix (`"06. PROD"`, `"07. SI"`), verified in the dev
 * mongo dump and relied on elsewhere in the platform (e.g.
 * eva-performance-calculation's `stat == "06. PROD" || stat == "07. SI"`). The
 * chart matches the field verbatim, so the prefixed form is what goes on the
 * wire. Note the prefix encodes the life-of-a-well STAGE, not a unique id — PROD
 * and INJ both share `06.`.
 *
 * The FULL vocabulary is offered, not just the statuses present in the current
 * filter: a user should be able to pick any of them, and a selection matching no
 * wells drawing an empty chart is a useful answer rather than a fault. That is
 * also why this is a constant rather than a fetch — the list does not depend on
 * what is filtered.
 *
 * Deliberately omitted:
 * - DISP and OBS — disposal/observation wells, not a status users filter by;
 * - the n/a / unknown buckets — absent or unclassifiable, not a status at all;
 * - **`"00. LOC"` (Location)** — pulled at product's request (2026-08-13): its
 *   count does not read correctly against the data, so offering it invites a
 *   selection whose answer is not trustworthy. Note this one IS present in real
 *   EVA data, unlike the others above, so it was previously restored here as an
 *   accidental omission; it is now an explicit decision, and this note exists so
 *   it is not "corrected" back a second time. Reinstate once the count is
 *   understood.
 *
 *   Accepted consequence: `resolveWellStatusSelection` validates against THIS
 *   list and falls back to `defaultWellStatusCurrentStatus()`, so a
 *   dashboard saved with LOC selected silently reopens showing Producing —
 *   different numbers, no error and no toast. Deliberate: the alternative is
 *   keeping an option whose count product has said is wrong, and the coercion
 *   lands on the default the widget would have had anyway.
 *
 *   The coercion is also PERMANENT, not just per-session: the controller resolves
 *   the saved status to the default, the response echo writes the resolved
 *   request back onto the chart entity, and the widget session persists it — so
 *   the next dashboard save overwrites the original selection. If LOC is ever
 *   reinstated, dashboards re-saved in the meantime cannot be restored to it.
 *
 * All four remain in {@link WELL_STATUS_DISPLAY_NAMES}: the server can still
 * RETURN any of them (they are real `header.status` values, and All Statuses mode
 * breaks out whatever the wells hold), so they still need readable names on the
 * axis and in the legend. Not selectable is not the same as not shown — the same
 * split the DRAIN variants have on the over-time chart. That claim was false when
 * written — only LOC had an entry, so DISP, OBS and n/a rendered as raw wire
 * values — and the map now carries all four.
 *
 * Display text is NOT stored here — it resolves through
 * WELL_STATUS_DISPLAY_NAMES, so a name lives in exactly one place and the
 * dropdown, axis titles and legend cannot drift apart.
 */
const AZURE_WELL_STATUS_CURRENT_OPTIONS: readonly string[] = [
  ALL_WELL_STATUSES,
  "01. LIC",
  "02. SPUD",
  "03. DUC",
  "04. CMPLTD",
  "06. DRAIN",
  "06. DRAIN (CMPLTD)",
  "06. DRAIN (INJ)",
  "06. DRAIN (PROD)",
  "06. DRAIN (SI)",
  "06. DRAIN (SUSP)",
  "06. INJ",
  "06. PROD",
  "07. SI",
  "08. IDLE",
  "08. SUSP",
  "09. ABD",
  "10. IDLE LIC"
];

/**
 * AWS (EVA-301): every Header.Status value eva-app-etl's header__status mapping
 * writes (UDM statuses as display text, no stage prefix), so these must match it.
 * Ordered as Novi Insights orders them (EVA-302); both Well Status charts offer
 * this one list.
 */
export const AWS_WELL_STATUSES = [
  "Active",
  "Completed",
  "Spud",
  "DUC",
  "Permit Approved",
  "Permit Submitted",
  "Permit Cancelled",
  "Inactive",
  "Abandoned",
  "P&A",
  "Unknown"
] as const;

export type AwsWellStatus = (typeof AWS_WELL_STATUSES)[number];

export const isAwsWellStatus = (value: Maybe<string>): value is AwsWellStatus =>
  (AWS_WELL_STATUSES as readonly Maybe<string>[]).includes(value);

// "Unknown" is returnable but not offered, like n/a above.
const AWS_WELL_STATUS_OVER_TIME_OPTIONS: readonly string[] = AWS_WELL_STATUSES.filter(
  (status) => status !== "Unknown"
);

const AWS_WELL_STATUS_CURRENT_OPTIONS: readonly string[] = [
  ALL_WELL_STATUSES,
  ...AWS_WELL_STATUS_OVER_TIME_OPTIONS
];

// A function, not a constant: this module loads app-wide, and tests mock IS_AWS_ENV
// through a getter that must not be read at import time.
export const wellStatusCurrentOptions = (): readonly string[] =>
  IS_AWS_ENV ? AWS_WELL_STATUS_CURRENT_OPTIONS : AZURE_WELL_STATUS_CURRENT_OPTIONS;

// Over Time never offers All Statuses, so on AWS it is the same list minus that entry.
export const wellStatusOverTimeOptions = (): readonly string[] =>
  IS_AWS_ENV ? AWS_WELL_STATUS_OVER_TIME_OPTIONS : WELL_STATUS_OVER_TIME_OPTIONS;

/**
 * Human-readable names for every status the widget can show, keyed by the WIRE
 * value.
 *
 * The wire values stay abbreviations — eva-data-svc-rs parses them with
 * `WellStatus::from_label` and chart-svc validates against the same set, so
 * "DUC" must keep travelling as "DUC". These names are display only: the
 * dropdowns, the axis titles and the stacked legend all read from here.
 *
 * Covers BOTH vocabularies (derived labels and stage-prefixed `header.status`)
 * plus the bare forms chart-svc emits after stripping the stage prefix, so one
 * lookup serves every surface.
 */
export const WELL_STATUS_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  [ALL_WELL_STATUSES]: "All Statuses",
  // Derived vocabulary (Status Over Time).
  LIC: "Licensed",
  "IDLE LIC": "Idle Licensed",
  // "Spud" and "DUC", NOT "Spudded"/"Drilled Uncompleted": product asked for the
  // industry shorthand on these two specifically, while
  // the rest keep their expanded names.
  SPUD: "Spud",
  IDLE: "Idle",
  DUC: "DUC",
  CMPLTD: "Completed",
  PROD: "Producing",
  INJ: "Injecting",
  SI: "Shut In",
  SUSP: "Suspended",
  ABD: "Abandoned",
  LOC: "Location",
  // Bare DRAIN and DRAIN (CMPLTD) are STORED header.status values that the derived
  // vocabulary has no equivalent for — they only appear on the current chart.
  DRAIN: "Drain Leg",
  "DRAIN (CMPLTD)": "Drain Leg (Completed)",
  "DRAIN (DUC)": "Drain Leg (Drilled Uncompleted)",
  "DRAIN (PROD)": "Drain Leg (Producing)",
  "DRAIN (INJ)": "Drain Leg (Injecting)",
  "DRAIN (SI)": "Drain Leg (Shut In)",
  "DRAIN (SUSP)": "Drain Leg (Suspended)",
  // NOT SELECTABLE, but returnable — so they still need names.
  //
  // All Statuses mode breaks out whatever the wells actually hold, so these
  // arrive from the server even though no dropdown offers them. Without an entry
  // they fall through to the raw wire value and render as "06. DISP" on the axis,
  // in the legend and in the tooltip — the exact drift this single-source map
  // exists to prevent. `wellStatusDisplayName` strips a stage prefix before its
  // second lookup, so the bare form covers every prefixed variant too.
  DISP: "Disposal",
  OBS: "Observation",
  // The aggregation's own sentinel for "no status recorded"
  // (eva-data-svc-rs `NOT_AVAILABLE`), which is a real series in All Statuses
  // mode on any source with gaps.
  "n/a": "N/A",
  // Stage-prefixed header.status values (Current Well Status).
  "00. LOC": "Location",
  "03. DUC": "DUC",
  "06. PROD": "Producing",
  "06. INJ": "Injecting",
  "07. SI": "Shut In",
  "09. ABD": "Abandoned"
};

/**
 * Deliberately NOT RigFracTimeScale: the Well Status chart offers Yearly and
 * Daily and drops Quarterly (product decision superseding the ticket's
 * "same time selector as rigs/frac").
 */
/**
 * Most columns the over-time chart draws. Pins the server's TRAILING_BUCKETS
 * (eva-data-svc-rs/src/models/well_status_buckets.rs) — the fixed scales return
 * this many, and a Custom range's granularity is chosen to fit inside it.
 */
export const WELL_STATUS_MAX_COLUMNS = 13;

export enum WellStatusTimeScale {
  Yearly = "Yearly",
  Monthly = "Monthly",
  Weekly = "Weekly",
  Daily = "Daily",
  Custom = "Custom"
}

export enum RecoveryPVIRadius {
  Gross = "Gross",
  SweetSpot = "Sweet Spot",
  LandingZone = "Landing Zone"
}

export enum RecoveryPVISource {
  AllSources = "All Sources",
  Private = "Private",
  EvaResearch = "EVA Research"
}

export enum TimeStep {
  Day = "day",
  Month = "month",
  Hybrid = "hybrid"
}

export enum EvaSource {
  Public = "Public",
  Private = "Private",
  Hybrid = "Hybrid"
}

export enum EvaSeriesKind {
  Historical = "historical",
  Forecast = "forecast",
  TypeWell = "typewell"
}

export enum EvaStatisticsKind {
  P10 = "p10",
  P50 = "p50",
  P90 = "p90",
  Average = "average"
}

export const enum CalculationMethod {
  Count = 0,
  Sum = 1,
  Average = 2,
  Min = 3,
  Max = 4
}

export const enum LiquidProductMinLogYaxis {
  Point1 = 0.1,
  Point01 = 0.01
}
